// src/views/InquiryForm.jsx
// Homepage "See what's available" form for the First-Time Reverse and
// HomeSafe Second cards. Posts to /api/intake with mode "inquiry" (product
// "ftr" | "hs2"), which lands it in marketing_leads tagged web +
// inbound-inquiry, shows in the CRM's Web Leads inbox, and fires the same
// urgent email alert as the other web leads. Rendered inside .rbr-land, so it
// reuses the landing page's field/button classes.
//
// Three short steps with a progress bar (10/13/26) — the statement intake's
// pattern: Step 1 (name + phone/email) creates the lead and fires the URGENT
// alert, so a visitor who stops there is still a lead. Each later Continue
// saves onto the SAME row (stage "complete", the id the server returned);
// the last step (or Skip) sends `final:true`, which emails Reece the details.
// The bar never reads full until the visitor is actually done.

import React, { useState, useEffect, useRef } from "react";
import * as BorrowerAPI from '../borrowerApi';

const CREDIT=["Excellent (740+)","Good (680–739)","Fair (620–679)","Below 620","Not sure"];
const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
const THIS_YEAR=new Date().getFullYear();
const YEARS=Array.from({length:60},(_,i)=>THIS_YEAR-45-i);   // ~45 to ~105 years old
const COPY={
  ftr:{eyebrow:"First-time reverse",title:"See what's available for your home",balance:"Current mortgage balance",balanceHint:"0 if your home is paid off"},
  hs2:{eyebrow:"HomeSafe Second",title:"See what a second could give you",balance:"First mortgage balance",balanceHint:"The loan you'd keep"},
};
const STEPS=3;
// Bar fill per step — starts partly full (people finish more often when they
// begin with some progress) and only reaches 100% on the thank-you screen.
const FILL={1:20,2:55,3:85};
const STEP_NOTE={1:"Takes about a minute",2:"Optional — helps me run accurate numbers",3:"Last step — optional"};
const SEND_FAIL="That didn't go through — please try again, or call or text 302-893-8949.";

const track=(name)=>{try{if(window.dataLayer&&window.dataLayer.push)window.dataLayer.push({event:name});if(typeof window.gtag==="function")window.gtag("event",name);}catch(e){}};

function InquiryForm({product,onClose}){
  const c=COPY[product]||COPY.ftr;
  const prod=product==="hs2"?"hs2":"ftr";
  const [f,setF]=useState({name:"",phone:"",email:"",dobM:"",dobD:"",dobY:"",address:"",diffMail:false,mailingAddress:"",propertyValue:"",balance:"",amountWanted:"",credit:"",notes:""});
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [done,setDone]=useState(false);
  const [step,setStep]=useState(1);
  // The lead id the server returned for Step 1 — every later save carries it
  // so it lands on the same row. `landed` = a write on that id went through
  // (until then the next save is still the first write, stage "start").
  const [leadId,setLeadId]=useState("");
  const [landed,setLanded]=useState(false);
  const topRef=useRef(null);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));

  // New step → back to the top of the pop-up (on a phone the next step's
  // heading would otherwise be scrolled off) and focus its first field.
  useEffect(()=>{
    const el=topRef.current;if(!el)return;
    try{el.scrollIntoView({block:"start"});}catch(e){}
    const first=el.parentNode&&el.parentNode.querySelector("[data-step-body] input, [data-step-body] select, [data-step-body] textarea");
    if(first&&step>1)try{first.focus({preventScroll:true});}catch(e){}
  },[step,done]);

  const dobOf=()=>{
    const anyDob=f.dobM||f.dobD||f.dobY;
    return anyDob&&f.dobM&&f.dobD&&f.dobY?f.dobY+"-"+String(f.dobM).padStart(2,"0")+"-"+String(f.dobD).padStart(2,"0"):"";
  };
  const payload=(stage,extra)=>Object.assign({
    mode:"inquiry",product:prod,stage:stage,id:leadId||undefined,
    name:f.name.trim(),phone:f.phone.trim(),email:f.email.trim(),
    address:f.address.trim(),mailingAddress:f.diffMail?f.mailingAddress.trim():"",
    propertyValue:f.propertyValue,balance:f.balance,credit:f.credit,goals:f.notes.trim(),
    dob:dobOf(),amountWanted:f.amountWanted,
  },extra||{});
  // One save. Returns true when it landed; shows the error otherwise.
  // Step 1 always saves as "start": on its own row that re-alerts Reece only
  // when the phone / email changed (← Back to fix a typo), never twice.
  const save=async(extra,stage)=>{
    const res=await BorrowerAPI.submitIntake(payload(stage||(landed?"complete":"start"),extra));
    // Honest failure: never move on / show "thanks" for a write that didn't land.
    if(!res||!res.ok){
      setErr(res&&res.error==="invalid_phone"?"That phone number doesn't look right — please check it, or add an email instead.":SEND_FAIL);
      return false;
    }
    if(res.id)setLeadId(res.id);
    setLanded(true);
    return true;
  };

  // Step 1 — name + a way to reach them. This write creates the lead.
  const next1=async()=>{
    setErr("");
    const digits=f.phone.replace(/\D/g,"");
    if(!f.name.trim()){setErr("Please add your name.");return;}
    if(!digits&&!f.email.trim()){setErr("Please add a phone number or an email so I can reach you.");return;}
    if(digits&&digits.length<10){setErr("That phone number looks short — please check it.");return;}
    setBusy(true);
    const ok=await save(null,"start");
    setBusy(false);
    if(!ok)return;
    track("inquiry_step1_"+product);
    setStep(2);
  };
  // Step 2 — the property address. Saved before moving on, so nothing typed
  // here is lost if they stop on Step 3.
  const next2=async()=>{
    setErr("");
    setBusy(true);
    const ok=await save();
    setBusy(false);
    if(!ok)return;
    track("inquiry_step2_"+product);
    setStep(3);
  };
  // Finish (Step 3's button, or Skip on Step 2 / 3) — the final save emails
  // Reece everything they gave. Date of birth (Step 3) is optional, but on
  // the button a started one has to be a whole, real date; on Skip a
  // half-finished one is simply left out (dobOf), never a reason to block.
  const finish=async(skipped)=>{
    setErr("");
    const anyDob=f.dobM||f.dobD||f.dobY;
    if(!skipped&&anyDob&&!(f.dobM&&f.dobD&&f.dobY)){setErr("Please finish your date of birth (month, day and year) — or leave it blank.");return;}
    if(!skipped&&anyDob&&f.dobM&&f.dobD&&f.dobY){const t=new Date(+f.dobY,+f.dobM-1,+f.dobD);if(t.getMonth()!==+f.dobM-1){setErr("That date of birth doesn't exist — please check the day.");return;}}
    setBusy(true);
    const ok=await save({final:true});
    setBusy(false);
    if(!ok)return;
    track((skipped?"inquiry_skip_":"inquiry_submit_")+product);
    setDone(true);
  };
  const back=()=>{setErr("");setStep(s=>Math.max(1,s-1));};

  const money=v=>{const d=String(v||"").replace(/[^0-9]/g,"");return d?Number(d).toLocaleString():"";};
  const lbl={display:"block"};

  return(<div onClick={()=>{if(!busy)onClose();}} style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.55)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"24px 14px",overflowY:"auto"}}>
    <div onClick={e=>e.stopPropagation()} role="dialog" aria-label={c.title} style={{background:"#fff",width:560,maxWidth:"100%",padding:"26px 26px 22px",borderTop:"4px solid var(--clay)",boxShadow:"0 18px 60px rgba(6,26,48,0.35)",marginBottom:30}}>
      <div ref={topRef} style={{display:"flex",alignItems:"flex-start",gap:12,scrollMarginTop:24}}>
        <div style={{flex:1}}>
          <div className="eyebrow">{c.eyebrow}</div>
          <h2 style={{fontSize:24,fontWeight:800,color:"var(--navy)",margin:"10px 0 0",lineHeight:1.15}}>{done?"Thank you — I've got it.":c.title}</h2>
        </div>
        <button onClick={onClose} aria-label="Close" style={{background:"none",border:"none",fontSize:28,lineHeight:1,cursor:"pointer",color:"var(--muted)"}}>×</button>
      </div>

      {/* Progress — Step N of 3; full only on the thank-you screen. */}
      <div data-inquiry-progress style={{marginTop:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,marginBottom:7}}>
          <span style={{fontSize:13,fontWeight:800,color:"var(--navy)",letterSpacing:.3}}>{done?"All done":"Step "+step+" of "+STEPS}</span>
          <span style={{fontSize:13,color:"var(--muted)",textAlign:"right"}}>{done?"Thank you":STEP_NOTE[step]}</span>
        </div>
        <div role="progressbar" aria-label="Form progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={done?100:FILL[step]} style={{height:8,background:"var(--cream)",border:"1px solid var(--rule)",overflow:"hidden"}}>
          <div style={{height:"100%",width:(done?100:FILL[step])+"%",background:"var(--navy)",transition:"width .35s ease"}}/>
        </div>
      </div>

      {done?<>
        <p className="lede" style={{marginTop:12}}>I'll look at your numbers personally and reach out — usually the same business day. No cost, no obligation.</p>
        <p className="lede">Want to talk sooner? Call or text <a href="tel:+13028938949" style={{color:"var(--navy)",fontWeight:800}}>302-893-8949</a>.</p>
        <button className="btn btn-primary" type="button" onClick={onClose} style={{marginTop:16}}>Done</button>
      </>:<>
        {step===1&&<div data-step-body>
        <p className="lede" style={{marginTop:14}}>Start with your name and the best way to reach you — that's all I need to get going. No cost, no obligation.</p>
        <div className="stack">
          <label style={lbl}><span className="field-label">Name *</span><input className="field" value={f.name} onChange={e=>u("name",e.target.value)} autoComplete="name"/></label>
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <label style={{...lbl,flex:"1 1 200px"}}><span className="field-label">Phone</span><input className="field" type="tel" value={f.phone} onChange={e=>u("phone",e.target.value)} autoComplete="tel" placeholder="(480) 555-0123"/></label>
            <label style={{...lbl,flex:"1 1 200px"}}><span className="field-label">Email</span><input className="field" type="email" value={f.email} onChange={e=>u("email",e.target.value)} autoComplete="email"/></label>
          </div>
          <p style={{fontSize:13.5,color:"var(--muted)",margin:"-4px 0 0"}}>A phone <em>or</em> an email is all I need.</p>
        </div>
        </div>}

        {step===2&&<div data-step-body>
        <p className="lede" style={{marginTop:14}}>Which home is this for? Optional — skip it if you'd rather cover it on the phone.</p>
        <div className="stack">
          <label style={lbl}><span className="field-label">Property address</span><input className="field" value={f.address} onChange={e=>u("address",e.target.value)} autoComplete="street-address" placeholder="Street, City, State ZIP"/></label>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13.5,color:"var(--muted)",cursor:"pointer",marginTop:-4}}>
            <input type="checkbox" checked={f.diffMail} onChange={e=>u("diffMail",e.target.checked)}/> My mailing address is different
          </label>
          {f.diffMail&&<label style={lbl}><span className="field-label">Mailing address</span><input className="field" value={f.mailingAddress} onChange={e=>u("mailingAddress",e.target.value)}/></label>}
        </div>
        </div>}

        {step===3&&<div data-step-body>
        <p className="lede" style={{marginTop:14}}>Your best guesses are fine — I'll confirm everything with you.</p>
        <div className="stack">
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <label style={{...lbl,flex:"1 1 200px"}}><span className="field-label">Estimated home value</span><input className="field" inputMode="numeric" value={money(f.propertyValue)} onChange={e=>u("propertyValue",e.target.value.replace(/[^0-9]/g,""))} placeholder="$"/></label>
            <label style={{...lbl,flex:"1 1 200px"}}><span className="field-label">{c.balance}</span><input className="field" inputMode="numeric" value={money(f.balance)} onChange={e=>u("balance",e.target.value.replace(/[^0-9]/g,""))} placeholder={c.balanceHint}/></label>
          </div>
          <label style={lbl}><span className="field-label">How much are you hoping to access? (optional)</span><input className="field" inputMode="numeric" value={money(f.amountWanted)} onChange={e=>u("amountWanted",e.target.value.replace(/[^0-9]/g,""))} placeholder="$"/></label>
          <div>
            <span className="field-label">Date of birth</span>
            <div style={{display:"flex",gap:8}}>
              <select className="field" aria-label="Birth month" value={f.dobM} onChange={e=>u("dobM",e.target.value)} style={{flex:"1.7 1 0",padding:"14px 8px",minWidth:0}}>
                <option value="">Month</option>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}
              </select>
              <select className="field" aria-label="Birth day" value={f.dobD} onChange={e=>u("dobD",e.target.value)} style={{flex:"1.1 1 0",padding:"14px 8px",minWidth:0}}>
                <option value="">Day</option>{Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
              </select>
              <select className="field" aria-label="Birth year" value={f.dobY} onChange={e=>u("dobY",e.target.value)} style={{flex:"1.3 1 0",padding:"14px 8px",minWidth:0}}>
                <option value="">Year</option>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <p style={{fontSize:13.5,color:"var(--muted)",margin:"6px 0 0"}}>{product==="hs2"?"A second is sized on your age — married? Use the younger spouse's.":"How much you can get depends on your age — married? Use the younger spouse's."}</p>
          </div>
          <div>
            <span className="field-label">Credit (your best guess)</span>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {CREDIT.map(x=>{const on=f.credit===x;return <button key={x} type="button" onClick={()=>u("credit",on?"":x)} style={{padding:"9px 13px",fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",border:"1.5px solid "+(on?"var(--navy)":"var(--rule)"),background:on?"var(--navy)":"#fff",color:on?"var(--cream)":"var(--navy)"}}>{x}</button>;})}
            </div>
          </div>
          <label style={lbl}><span className="field-label">Anything else? (optional)</span>
            <textarea className="field" rows={3} value={f.notes} onChange={e=>u("notes",e.target.value)} placeholder="What you'd use the money for, questions, best time to reach you…" style={{resize:"vertical",minHeight:80}}/>
          </label>
        </div>
        </div>}

        {err&&<div className="err" role="alert" style={{textAlign:"left",marginTop:12,fontWeight:700}}>{err}</div>}
        <button className="btn btn-primary" type="button" onClick={step===1?next1:step===2?next2:()=>finish(false)} disabled={busy} style={{marginTop:18,opacity:busy?.7:1}}>
          {busy?(step===3?"Sending…":"One moment…"):step===1?"Continue":step===2?"Continue — 1 more step":"See what's available"}
        </button>
        {step>1&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginTop:12,flexWrap:"wrap"}}>
          <button type="button" onClick={back} disabled={busy} style={{background:"none",border:"none",padding:"8px 0",fontFamily:"inherit",fontSize:14,fontWeight:700,color:"var(--navy)",cursor:"pointer"}}>← Back</button>
          <button type="button" onClick={()=>finish(true)} disabled={busy} style={{background:"none",border:"none",padding:"8px 0",fontFamily:"inherit",fontSize:14,fontWeight:600,color:"var(--muted)",textDecoration:"underline",textUnderlineOffset:3,cursor:busy?"default":"pointer"}}>Skip — I'll cover it on the call</button>
        </div>}
        <p className="cta-sub">Your information goes only to me. I'll never sell it.</p>
      </>}
    </div>
  </div>);
}

export { InquiryForm };
